<template>
  <div id="app">
    <transition name="export-bar-slide">
      <div class="global-export-bar" v-if="exportStatus" :class="exportStatus">
        <div class="global-export-content">
          <i v-if="exportStatus === 'exporting'" class="el-icon-loading"></i>
          <i v-else-if="exportStatus === 'success'" class="el-icon-circle-check"></i>
          <i v-else-if="exportStatus === 'error'" class="el-icon-circle-close"></i>
          <span>{{ exportBarText }}</span>
        </div>
        <div class="global-export-progress" v-if="exportStatus === 'success'" :class="{active: exportProgressActive}"></div>
      </div>
    </transition>
    <div class="permanent-top-gap"></div>
    <!-- 手机浏览器打开时问一次是否跳转 App（只提示，不拦页面） -->
    <OpenAppPrompt />
    <router-view  />
  </div>
</template>
<script>
// Supports weights 100-900
import "@fontsource-variable/inter/opsz.css";
// Italic serif used by bo2-discuss-quote / bo2-finding-quote
import "@fontsource-variable/fraunces/opsz-italic.css";
import { mountLibredesk } from "@/utils/libredesk.js";
import OpenAppPrompt from "@/components/common/OpenAppPrompt.vue";


export default {
  name: "App",
  components: { OpenAppPrompt },
  data() {
    return {
      exportProgressActive: false,
      exportDismissTimer: null,
    };
  },
  computed: {
    exportStatus() {
      return this.$store.state.exportStatus.status;
    },
    exportBarText() {
      if (this.exportStatus === 'exporting') return this.$t('message.Exporting') || 'Exporting, please wait...';
      if (this.exportStatus === 'success') return this.$t('message.Download_ready') || 'Download ready';
      if (this.exportStatus === 'error') return this.$store.state.exportStatus.errorMsg || this.$t('message.Export_failed') || 'Export failed';
      return '';
    },
  },
  created() {
    // 客服挂件:启动就以游客身份挂上,所以登录页也能找客服。
    // 登录后由 auth/me 调 identifyLibredesk 升级为该用户,不重建挂件。
    mountLibredesk();
    // Reset global time filter state on app start
    this.$store.commit('userTimeFilter/clearAllTime')
  },
  mounted() {
    // Global store state initialization — reset view modes and platform selections
    // to their default values on every app load.
    this.$store.commit('restaurant/change_ReviewShowType',false);
    this.$store.commit('reviewReply/change_ReviewShowType',false);
    // Initialize default platform selections for each review module
    // 只默认勾选 6 个核心 OTA。LUXURYESCAPES / INTERNAL / MAKEMYTRIP / HOTELDE 是长尾渠道，只有个别酒店接了，
    // 默认勾上会让绝大多数酒店的列表里混进一路几乎没数据的来源 —— 需要看的人自己点开那张卡片。
    this.$store.dispatch('reviewReply/updateSelectedPlatforms',['BOOKING','TRIP','EXPEDIA','TRIPADVISOR','GOOGLE','AGODA']);
    this.$store.dispatch('dashBoard/updateSelectedPlatforms',['BOOKING','TRIP','EXPEDIA','TRIPADVISOR','GOOGLE','AGODA']);
    this.$store.commit('restaurant/SET_selectedPlatforms',['TRIPADVISOR','GOOGLE','OPENTABLE','YELP','WONGNAI']);
    // Apply lang-* class to <html> so language-aware CSS tokens take effect
    this.applyLangClass();
    // Apply is-mac class to <html> so macOS font-rendering tweaks can target it
    this.applyOSClass();
  },
  watch: {
    '$i18n.locale': 'applyLangClass',
    exportStatus(val) {
      clearTimeout(this.exportDismissTimer);
      if (val === 'success') {
        this.exportProgressActive = false;
        this.$nextTick(() => { this.exportProgressActive = true; });
        this.exportDismissTimer = setTimeout(() => {
          this.clearExport();
          this.exportProgressActive = false;
        }, 3000);
      } else if (val === 'error') {
        this.exportProgressActive = false;
        this.exportDismissTimer = setTimeout(() => {
          this.clearExport();
        }, 5000);
      }
    },
  },
  methods: {
    clearExport() {
      this.$store.commit('exportStatus/CLEAR');
    },
    // Swap the lang-* class on <html> to match the current locale.
    // This drives the language-aware CSS token overrides defined in App.vue.
    applyLangClass() {
      const html = document.documentElement;
      html.classList.forEach(c => { if (c.startsWith('lang-')) html.classList.remove(c); });
      html.classList.add('lang-' + (this.$i18n.locale || 'en'));
    },
    // macOS renders the same font-size visibly smaller than Windows, so
    // some fonts need a size bump there — flag it once via an <html> class.
    applyOSClass() {
      const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || '';
      if (/Mac/i.test(platform)) {
        document.documentElement.classList.add('is-mac');
      }
    },
  },
};
</script>

<style lang="scss">

/* 键盘焦点框：只在用键盘操作时（html.kbd-nav，见 utils/keyboardNav.js）显示 2px 紫框 + 淡紫外圈；
   鼠标点击不出任何框。!important 压过各处的 outline: none / box-shadow: none（如 el-input 聚焦样式）。
   Element 的复选框 / 单选框真正获得焦点的是隐藏的 <input>，框画在它旁边看得见的那个元素上 */
html.kbd-nav :focus-visible,
html.kbd-nav .el-checkbox__input.is-focus .el-checkbox__inner,
html.kbd-nav .el-radio__input.is-focus .el-radio__inner,
html.kbd-nav .el-radio-button__orig-radio:focus-visible + .el-radio-button__inner,
html.kbd-nav .el-switch__input:focus-visible + .el-switch__core {
  outline: 2px solid #7B61C4 !important;
  outline-offset: 0 !important;
  box-shadow: 0 0 0 6px rgba(123, 97, 196, 0.25) !important;
}
/* 放在会裁切的容器里的按钮（v-kbd-toggle 的 inset），焦点框画在内侧，不被裁掉 */
html.kbd-nav .kbd-ring-inset:focus-visible {
  outline-offset: -2px !important;
  box-shadow: inset 0 0 0 6px rgba(123, 97, 196, 0.25) !important;
}
/* 能点的卡片：鼠标经过边框加深 + 轻阴影 + 手型光标，不上浮（纯展示卡片不加这个类，完全静止）。
   DashboardCard 传 clickable 会自动加上；手写的卡片能点时也用这个类 */
.wtk-card-clickable {
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.wtk-card-clickable:hover {
  border-color: var(--color-border-strong, #D1D5DB) !important;
  box-shadow: var(--shadow-hover, 0 4px 12px rgba(28, 25, 23, 0.08)) !important;
}
/* 鼠标点击（不在键盘模式）时连浏览器默认的焦点框也不要 */
html:not(.kbd-nav) :focus-visible {
  outline: none;
}

:root {
  /* Height of the fixed mobile/tablet top bar (Navbar.vue .xs-topbar, shown
     below 1024px). Sticky page headers add this as their `top` offset so
     they dock under the bar instead of behind it (the bar's z-index:1011
     otherwise paints over anything stuck at top:0). 0px above 1024px, where
     that bar doesn't render at all. */
  --xs-topbar-h: 0px;
  --bg: #F5F3EE;
  --card: #FFFFFF;
  --primary: #1A365D;
  --primary-light: #2A4A7F;
  --accent: #1A365D;
  --accent-soft: #EBF8FF;
  --text: #1A1A1A;
  --text-muted: #6B7280;
  --border: #E8E4DC;
  --ok: #2D8B55;
  --warn: #E0922F;
  --bad: #C0392B;
  --info: #2C6FAC;
  --tag-blue: #E8F0FE;
  --tag-green: #E6F4EA;
  --tag-yellow: #FEF7E0;
  --tag-red: #FCE8E6;
  --shadow-sm: 0 1px 4px rgba(0, 0, 0, 0.04);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 8px 24px rgba(0, 0, 0, 0.12);
  --radius: 14px;
  --radius-sm: 10px;
  --radius-lg: 20px;

  /* Typography scale */
  --font-primary: 'Inter Variable', Inter, sans-serif;
  --text-xs: 12px;
  --text-sm: 13px;
  --text-base: 14px;
  --text-md: 16px;
  --text-lg: 18px;
  --text-xl: 22px;
  --text-2xl: 28px;

  /* Spacing tokens */
  --gap-section: 24px;
  --gap-block: 16px;
  --gap-inner: 12px;

  /* Tooltip / help-bubble tokens — used by all explanatory dark tooltips */
  --tooltip-bg: #1F2A3A;
  --tooltip-text: #ffffff;
  --tooltip-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  --tooltip-radius: 8px;

  /* Sidebar design tokens — wired to biz-* so they follow module switching */
  --sidebar-bg: #FCFBF8;
  --sidebar-border: #E5E7EB;
  --sidebar-text: #1F2937;
  --sidebar-text-muted: #6B7280;
  --sidebar-text-subtle: #9CA3AF;
  --sidebar-hover: var(--biz-hover-bg, #F0F4F8);
  --sidebar-active-bg: var(--biz-selected-bg, #E6EEF6);
  --sidebar-active-text: var(--biz-selected-text, #2F4E85);
  --sidebar-accent: var(--biz-color, #2F4E85);

  /* Semantic text colors */
  --text-primary: #1F2937;
  --text-body: #374151;
  --text-secondary: #6B7280;
  --text-subtle: #9CA3AF;
  --text-link: #2563EB;

  /* Spacing scale */
  --space-xs: 4px;
  --space-sm: 8px;
  --space-md: 12px;
  --space-base: 16px;
  --space-lg: 24px;
  --space-xl: 32px;

  /* ── WTK Color System ── */
  /* Backgrounds */
  --color-bg-page:          #F5F2EA;
  --color-bg-card:          #FFFFFF;
  --color-bg-surface:       #F8F6F1;
  --color-bg-hover:         #F1EDE6;
  --color-bg-active:        #EDE8E0;

  /* Text */
  --color-text-primary:     #1C1917;
  --color-text-secondary:   #6B7280;
  --color-text-tertiary:    #9CA3AF;
  --color-text-inverse:     #FFFFFF;

  /* Accent / Brand */
  --color-accent-primary:   #1A365D;
  --color-accent-hover:     #15294A;
  --color-accent-light:     #EEF2F7;

  /* Semantic */
  --color-success:          #2F8F5B;
  --color-success-light:    #DCFCE7;
  --color-warning:          #D97706;
  --color-warning-light:    #FEF3C7;
  --color-error:            #DC2626;
  --color-error-light:      #FEE2E2;

  /* Borders / Dividers */
  --color-border:           #E5E7EB;
  --color-border-strong:    #D1D5DB;
  --color-divider-subtle:   #ECE7DE;

  /* Shadows */
  --shadow-card:            0 1px 3px rgba(28, 25, 23, 0.06);
  --shadow-hover:           0 4px 12px rgba(28, 25, 23, 0.08);

  /* ── Business Color (module-aware, default = hotel/blue) ──
     --biz-color 是填充色（按钮、勾选框、开关、标记、边框），--biz-text 是文字色。
     酒店模式两个一样；餐厅模式填充用浅橙、文字用深橙（浅橙当文字太浅，见下面餐厅覆盖） */
  --biz-color:              #1A365D;
  --biz-text:               #1A365D;
  --biz-color-hover:        #2A4A7A;
  --biz-hover-bg:           #F0F4F8;
  --biz-selected-bg:        #E6EEF6;
  --biz-selected-text:      #1A365D;
  --biz-border-hover:       #1A365D;
  --biz-focus-ring:         rgba(26, 54, 93, 0.2);
  --biz-tag-bg:             #E6EEF6;
  --biz-tag-text:           #1A365D;
  --biz-chart-color:        #1E3A8A;
  --biz-icon-color:         #1A365D;
  --biz-highlight:          #1A365D;

  /* ── Language-aware form layout tokens (default = EN) ── */
  --label-width:            160px;  /* FromLabel el-form-item label column */
  --tone-col-min-width:     145px;  /* ReplySetting Tone grid column */
}

/* ── Brand module overrides ──
   品牌模式只换侧边栏这一块的皮（底色白色、金色强调；选中态 2026-11-02 起由深绿换回品牌蓝，
   和全站 --biz-* 同一套蓝：--brand-primary = #1A365D，选中底 / 悬停底 = --biz-selected-bg / --biz-hover-bg），
   刻意不动 --biz-* —— 那一套被全站的按钮/日期选择器/表格用着，改了等于把整个页面
   也一起染色，而设计稿只要求左侧菜单变。 */
html[data-module="brand"] {
  --brand-primary:          #1A365D;  /* 模块 tab 选中底色，非侧边栏通用 token */
  --sidebar-bg:             #FFFFFF;
  --sidebar-hover:          #F0F4F8;
  --sidebar-active-bg:      #E6EEF6;
  --sidebar-active-text:    #1A365D;
  --sidebar-accent:         #A38856;

  /* 下拉框选项 hover / 选中态单独换成淡绿（沿用侧边栏 hover / 选中色），同样不碰 --biz-* */
  .el-select-dropdown__item.hover,
  .el-select-dropdown__item:hover,
  .el-month-table td .cell:hover,
  .el-year-table td .cell:hover {
    background-color: var(--sidebar-hover) !important;
  }
  .el-select-dropdown__item.selected {
    background-color: var(--sidebar-active-bg) !important;
    color: var(--sidebar-active-text) !important;
  }
}

/* ── Restaurant module overrides ── */
html[data-module="restaurant"] {
  /* 餐厅橙：填充 #F08A6C（勾选框、开关、标记、按钮），文字一律深橙 #B4541A（与 App 统一） */
  --biz-color:              #F08A6C;
  --biz-text:               #B4541A;
  --biz-color-hover:        #E47B5D;
  --biz-color-active:       #C2410C;
  --biz-hover-bg:           #FFF7ED;
  --biz-selected-bg:        #FFEDD5;
  --biz-selected-text:      #B4541A;
  --biz-border-hover:       #F08A6C;
  --biz-focus-ring:         rgba(240, 138, 108, 0.25);
  --biz-tag-bg:             #FFF7ED;
  --biz-tag-text:           #B4541A;
  --biz-chart-color:        #F08A6C;
  --biz-icon-color:         #F08A6C;
  --biz-highlight:          #F08A6C;
  --color-accent-light:     #FFF7ED;

  /* Element UI global overrides for restaurant theme */
  .el-input__inner:hover,
  .el-date-editor:hover {
    border-color: #F08A6C !important;
  }
  .el-input__inner:focus,
  .el-select .el-input.is-focus .el-input__inner {
    border-color: #D9D9D9 !important;
  }
  .el-select .el-input.is-focus .el-input__inner:hover {
    border-color: #F08A6C !important;
  }
  .el-checkbox__input.is-checked .el-checkbox__inner {
    background-color: #F08A6C !important;
    border-color: #F08A6C !important;
  }
  .el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: #F08A6C !important;
    border-color: #F08A6C !important;
  }
  .el-select-dropdown__item.selected {
    color: #B4541A !important;
  }
  .el-checkbox__input.is-checked + .el-checkbox__label {
    color: #B4541A !important;
  }
  /* Radio */
  .el-radio__input.is-checked .el-radio__inner {
    border-color: #F08A6C !important;
    background: #fff !important;
  }
  .el-radio__input.is-checked .el-radio__inner::after {
    background-color: #F08A6C !important;
    width: 6px !important;
    height: 6px !important;
  }
  .el-radio__input.is-checked + .el-radio__label {
    color: #B4541A !important;
  }
  /* Switch */
  .el-switch.is-checked .el-switch__core {
    border-color: #F08A6C !important;
    background-color: #F08A6C !important;
  }
  /* TDesign select dropdown */
  .t-select-option.t-is-selected {
    color: #B4541A !important;
  }
  .t-select-option.t-is-selected:not(.t-is-disabled) {
    background-color: #FFF7ED !important;
  }
  .t-select-option:hover:not(.t-is-disabled) {
    background-color: #FFF7ED !important;
  }
  /* TDesign checkbox */
  .t-checkbox__input:checked,
  .t-is-checked .t-checkbox__input {
    background-color: #F08A6C !important;
    border-color: #F08A6C !important;
  }
  .t-checkbox:hover .t-checkbox__input {
    border-color: #F08A6C !important;
  }
  .t-checkbox.t-is-checked .t-checkbox__label {
    color: #B4541A !important;
  }
  /* Element UI select dropdown */
  .el-select-dropdown__item.hover,
  .el-select-dropdown__item:hover {
    background-color: #FFF7ED !important;
  }
  .el-select-dropdown__item.selected {
    color: #B4541A !important;
  }
}

/* Per-locale overrides applied via html.lang-* class */
html.lang-en {
  --label-width:            160px;
  --tone-col-min-width:     145px;
}
html.lang-de {
  --label-width:            185px;
  --tone-col-min-width:     195px;
}
html.lang-zh {
  --label-width:            105px;
  --tone-col-min-width:     130px;
}
html.lang-it {
  --label-width:            175px;
  --tone-col-min-width:     160px;
}

@font-face {
  font-family: "Inter";
  src: url("@/assets/font/Inter-Regular.ttf") format("truetype");
}

@font-face {
  font-family: "InterMedium";
  src: url("@/assets/font/Inter-Medium.ttf") format("truetype");
}
// 数字换成 Geist：必须紧跟在上面两个 Inter 字体后面，说明见这个文件
@import "~@/style/geistDigits";


.languageBox {
  position: fixed;
  top: 0px;
  right: 50px;
  z-index: 10000;
  background: transparent;
  display: flex;
  justify-content: space-between;
  gap: 0 20px;
  line-height: 1;
  div {
    cursor: pointer;
  }
  p {
    background: #fff;
    margin: 0;
  }
}
/* @media (max-width: 850px) {
   body {
     overflow-x: auto;
   }
 }
 !* 屏幕宽度≤1000px时生效 *!
 @media (max-width: 1000px) {
   html {
     !* 固定font-size为1000px时的数值，停止缩放 *!
     font-size: 10px;
   }
 }*/
#app {
  font-family: Inter, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  // text-align: center;
  color: var(--text-body);
  font-size: var(--text-base);
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  height: 100%;
  background: var(--color-bg-page) !important;
  min-height: 100vh;
  width: 100%;
  transition: background 0.1s linear;
  // .router-view-container {
  //   margin-left: 252px;
  // }
}
*{
  font-family: Inter, sans-serif !important;
  word-break: normal; /* 仅在允许的断字点换行（默认值） */
  overflow-wrap: normal;
}

/* ===== Global Typography Hierarchy =====
 *
 * L1        .page-title           28px / 600  — page-level heading
 * L2        .analytics-module-title 18px / 700 — module / analytics section title  ← CANONICAL L2
 * L3        .card-title           16px / 600  — standard card or subsection title
 * L3-small  .card-title-compact   14px / 600  — compact card title (dense grids)
 * L4        .block-label          14px / 500  — label, small heading, UI metadata
 *           .meta-text            12px / 400  — timestamps, meta info
 *           .helper-text          13px / 400  — secondary helper text
 * L5        .body-text            14px / 400  — standard body copy
 *
 * Semantic rules:
 *   - .card-title-compact is a reduced TITLE, not a label.
 *     Do NOT use .block-label for card headers, even small ones.
 *   - Blue is NOT a title color. Blue = links, buttons, interactive elements only.
 *   - .section-title is DEPRECATED. Downgraded to L3 (16px/600).
 *     Migrate to .analytics-module-title (L2) or .card-title (L3).
 *   - .card-section-title kept as L3 alias for backward compatibility.
 *   - Avoid off-scale values: 15px, 17px, 19px, 30px, 32px.
 *
 * Hotel Overview mapping:
 *   .page-title            → "Hotel Dashboard"
 *   .analytics-module-title → Platform Overview, Performance Analytics,
 *                             Staff Mentions Leaderboard, Sentiment Evolution,
 *                             24h Activity Overview, Last week's top lists,
 *                             Top Markets, Languages, Traveller Type
 *   .card-title-compact    → Overall Performance, Location & Neighbourhood,
 *                             Cleanliness, Room Comfort, Hotel Amenities & Atmosphere,
 *                             Food & Beverage, Guest Experience & Service,
 *                             Value for Money
 */

/* L1 — Page title */
.page-title {
  font-size: var(--text-2xl, 28px);
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  line-height: 36px;
  letter-spacing: -0.3px;
}

/* L2 — Module / analytics section title (canonical L2 standard) */
.analytics-module-title {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: var(--text-primary, #1F2937);
}

/* L3 — Standard card / subsection title */
.card-title {
  font-size: var(--text-md, 16px);
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  line-height: 24px;
}

/* L3-small — Compact card title for dense grids
 * Use when many cards appear in a tight layout and 16px would feel heavy.
 * Still a title — not a label. Do not substitute with .block-label. */
.card-title-compact {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  line-height: 20px;
}

/* L3 alias — kept for backward compatibility */
.card-section-title {
  font-size: var(--text-md, 16px);
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  line-height: 24px;
}

/* DEPRECATED — .section-title was previously L2 (20px/600).
 * Downgraded to L3 (16px/600) to eliminate conflict with .analytics-module-title.
 * Still referenced in: ScoreImprovement.vue (inactive), CirPie/Morning.vue,
 * RestaurantDashboard/Morning.vue — do not delete until those are migrated.
 * Do not use for new module-level titles. */
.section-title {
  font-size: var(--text-md, 16px);
  font-weight: 600;
  color: var(--text-primary, #1F2937);
  line-height: 24px;
}

/* L4 — Label / small heading */
.block-label {
  font-size: var(--text-base, 14px);
  font-weight: 500;
  color: var(--text-secondary, #6B7280);
  line-height: 20px;
}

/* L5 — Body text */
.body-text {
  font-size: var(--text-base, 14px);
  font-weight: 400;
  color: var(--text-primary, #1F2937);
  line-height: 22px;
}

/* L6 — Helper / secondary text */
.helper-text {
  font-size: 13px;
  font-weight: 400;
  color: var(--text-secondary, #6B7280);
  line-height: 20px;
}

/* L6 — Meta / timestamp text */
.meta-text {
  font-size: 12px;
  font-weight: 400;
  color: var(--text-secondary, #6B7280);
  line-height: 18px;
}

/* Global el-table typography baseline */
.el-table th .cell {
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
}
.el-table td .cell {
  font-size: var(--text-sm);
  font-weight: 400;
  color: var(--text-body);
}

/* ── Sort-arrow clipping fix ──────────────────────────────────────────────
 * Element UI places header text and .caret-wrapper inside the same .cell.
 * When .cell has overflow:hidden + text-overflow:ellipsis, the sort arrows
 * get clipped.  Flex layout lets the text truncate independently while the
 * caret stays fully visible.
 * ─────────────────────────────────────────────────────────────────────── */
.el-table th.is-sortable .cell {
  display: flex !important;
  align-items: center;
}
.el-table th.is-sortable .cell > .caret-wrapper {
  flex-shrink: 0;
  margin-left: 8px;
}
.el-table th.is-sortable .cell > :not(.caret-wrapper) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Sort arrow active color: brand ─────────────────────────── */
.el-table th.ascending .sort-caret.ascending {
  border-bottom-color: var(--biz-color) !important;
}
.el-table th.descending .sort-caret.descending {
  border-top-color: var(--biz-color) !important;
}
/* ── Table row hover: follow biz theme (covers fixed-left/right columns) ─── */
.el-table__body tr:hover > td,
.el-table__body tr.hover-row > td,
.el-table__fixed .el-table__body tr.hover-row > td,
.el-table__fixed-right .el-table__body tr.hover-row > td {
  background-color: var(--biz-hover-bg) !important;
}

.global-export-bar {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2100;
  min-width: 320px;
  max-width: 480px;
  border-radius: 10px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 500;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  overflow: hidden;

  .global-export-content {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    i { font-size: 20px; }
  }

  .global-export-progress {
    height: 3px;
    background: rgba(255,255,255,0.4);
    width: 100%;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      transform: scaleX(0);
      transform-origin: right;
      background: rgba(255,255,255,0.7);
    }
    &.active::after {
      transform: scaleX(1);
      transition: transform 3s linear;
      transform-origin: right;
    }
  }

  &.exporting {
    background: #E5F0FF;
    border: 1px solid #E3E5F6;
    color: #4485E9;
    i { color: #4485E9; }
  }
  &.success {
    background: #f0f9eb;
    border: 1px solid #eaf6e3;
    color: #74c971;
    i { color: #74c971; }
    .global-export-progress {
      background: #74c971;
      &::after { background: #f0f9eb; }
    }
  }
  &.error {
    background: #FEE2E2;
    border: 1px solid #FCA5A5;
    color: #991B1B;
    i { color: #DC2626; }
  }
}
.export-bar-slide-enter-active, .export-bar-slide-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.export-bar-slide-enter, .export-bar-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.permanent-top-gap {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 10px !important;
  background-color: var(--color-bg-page) !important;
  z-index: 1999 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
}
/* Below 1024px the mobile/tablet top bar (Navbar.vue .xs-topbar, z-index 1011)
   fills this same top strip with its own solid background — this fixed gap's
   higher z-index (1999) painted over it, leaving a visible page-background
   seam across the top of the bar. Hide it whenever that bar is in charge. */
@media (max-width: 1023px) {
  .permanent-top-gap {
    display: none !important;
  }
  :root {
    --xs-topbar-h: 56px;
  }
}
.el-drawer__wrapper{
  //z-index: 3010 !important;
}
//
//router-view,
//#app > div:not(.permanent-top-gap) {
//  margin-top: 10px !important;
//}

*,
*::before,
*::after {
  box-sizing: border-box;
}

body,
html {
  margin: 0;
  padding: 0;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: auto;
}

html {
  // min-width:  768px; /* viewport-level horizontal scrollbar on narrow screens */
}

/* ── Global input hover/focus ──────────────────────────────── */
.el-date-editor.el-range-editor:hover {
  border-color: var(--biz-border-hover) !important;
}
.el-date-editor.el-range-editor.is-active,
.el-date-editor.el-range-editor:focus-within,
.el-radio__inner:hover,
.el-textarea__inner:hover,
.el-textarea__inner:focus {
  border-color: var(--biz-border-hover) !important;
}

.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover{
  background-color: var(--biz-hover-bg) !important;
  color: #232323 !important;
  border-radius: 4px;
}
.el-select-dropdown__item.selected {
  background-color: var(--biz-selected-bg) !important;
  color: var(--biz-text) !important;
  border-radius: 4px;
}
/* ── Date / Calendar Picker Global Styles ──────────────────────────────── */
/* All picker overrides are consolidated here.
   Panels are portaled to <body>, so scoped styles don't reach them.
   Rules are intentionally global. Keep this block self-contained.        */

/* Stable popup / panel widths — prevent shifting when month changes.
   min-width ensures correct dimensions from the first frame the element
   is inserted into the DOM, before Popper.js reads offsetWidth.           */
.el-date-range-picker {
  width: 646px !important;
  min-width: 646px !important;
  border-radius: 8px !important;
  overflow: hidden !important;
}
.el-date-range-picker.has-sidebar {
  width: max-content !important;
  min-width: unset !important;
}
.el-date-range-picker__content {
  box-sizing: border-box;
}

/* Colour-only overrides — keep Element UI's default cell layout intact */
.el-picker-panel .el-date-table td {
  font-family: "Inter Variable", sans-serif;
  font-size: 14px;
  color: #000;
  font-weight: 500;
}
.el-picker-panel .el-date-table td.today span           { font-weight: bold; color: var(--biz-text) !important; background-color: var(--biz-hover-bg) !important; border-radius: 4px; }
.el-picker-panel .el-date-table td.available:not(.start-date):not(.end-date):not(.current):hover span  { color: #000 !important; background-color: transparent !important; outline: 1px solid var(--biz-color); outline-offset: -1px; border-radius: 3px; }
.el-picker-panel .el-date-table td.disabled div          { background-color: #F5F5F4 !important; }
.el-picker-panel .el-date-table td.disabled span         { color: #D1D0CE; font-weight: 400; }
.el-picker-panel .el-date-table td.prev-month span,
.el-picker-panel .el-date-table td.next-month span       { color: #D1D0CE; font-weight: 400; }
.el-picker-panel .el-date-table td.start-date span,
.el-picker-panel .el-date-table td.start-date:hover span,
.el-picker-panel .el-date-table td.end-date span,
.el-picker-panel .el-date-table td.end-date:hover span,
.el-picker-panel .el-date-table td.current:not(.disabled) span { background: var(--biz-color) !important; color: #fff !important; border-radius: 3px; }

/* Range highlight band */
.el-date-table td.in-range div,
.el-date-table td.in-range div:hover,
.el-date-table.is-week-mode .el-date-table__row.current div,
.el-date-table.is-week-mode .el-date-table__row:hover div {
  background-color: var(--biz-hover-bg) !important;
  color: rgba(35, 35, 35, 1);
}

/* Rounded ends on range band */
.el-picker-panel .el-date-table td.start-date div {
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
}
.el-picker-panel .el-date-table td.end-date div {
  border-top-right-radius: 3px;
  border-bottom-right-radius: 3px;
}

/* Weekday header row */
.el-picker-panel .el-date-table th {
  color: #232323;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  border-bottom: none;
}

/* Month / year selection grid cells */
.el-month-table td .cell,
.el-year-table td .cell {
  color: #333;
  font-size: 14px;
  &:hover { background-color: var(--biz-hover-bg); }
}

/* ── Picker header ─────────────────────────────────────────────────────── */
.el-picker-panel__header {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 40px !important;
  padding: 0 10px !important;
  color: #000;
  font-size: 14px !important;
  font-weight: 500 !important;
}

/* Month/year clickable labels in header */
.el-picker-panel__link-btn {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #000 !important;
  line-height: 1 !important;
}

/* Navigation arrow buttons */
.el-picker-panel__icon-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  margin: 0 !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 4px !important;
  color: #333 !important;
  cursor: pointer;
  &::before { color: #333 !important; font-size: 14px !important; }
}
.el-picker-panel__icon-btn:hover {
  color: var(--biz-text) !important;
  background-color: var(--biz-hover-bg) !important;
  &::before { color: var(--biz-text) !important; }
}

/* Range picker header — clear floats first, then use flex for vertical centering */
.el-date-range-picker__header {
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
  box-sizing: border-box !important;
  height: 40px !important;
  padding: 0 10px !important;
}
/* Element UI floats these buttons by default — must unset so flex can take over */
.el-date-range-picker__header button {
  float: none !important;
}
/* DOM order is: [<<][<][>>][>][div] — reorder so div sits between left and right buttons
   Target order: [<<(1)][<(2)]  [div(3)]  [>(4)][>>(5)] */
.el-date-range-picker__header .el-icon-d-arrow-left  { order: 1 !important; }
.el-date-range-picker__header .el-icon-arrow-left    { order: 2 !important; }
.el-date-range-picker__header .el-icon-arrow-right   { order: 4 !important; }
.el-date-range-picker__header .el-icon-d-arrow-right { order: 5 !important; }
.el-picker-panel .el-date-range-picker__content .el-date-range-picker__header div {
  flex: 1;
  order: 3;
  text-align: center;
  white-space: nowrap;
  margin: 0 !important;
  font-size: 14px !important;
  font-weight: 500;
  color: #000 !important;
  font-family: "Inter Variable", sans-serif;
  line-height: 1 !important;
}

/* Make sidebar + body use flex so sidebar can be auto-width */
.el-picker-panel__body-wrapper {
  display: flex !important;
}

/* ── Shortcuts sidebar (left presets panel inside range picker popup) ────── */
.el-picker-panel__sidebar {
  position: relative !important;
  border-right: 1px solid #E4E7ED;
  padding: 16px 0 8px 0;
  background: #fff;
  width: max-content !important;
  min-width: unset !important;
  flex-shrink: 0;
}
.el-picker-panel__shortcut {
  display: flex;
  align-items: center;
  width: 100%;
  border: 0;
  background: transparent;
  font-family: "Inter Variable", Inter, sans-serif;
  font-size: 13px;
  height: 40px;
  color: #232323;
  padding: 0 16px 0 12px;
  cursor: pointer;
  text-align: left;
  outline: 0;
  white-space: nowrap;
  &:hover {
    background-color: var(--biz-hover-bg);
    color: var(--biz-text) !important;
  }
  &.active,
  &:active,
  &:focus {
    color: var(--biz-text) !important;
    font-weight: 600;
  }
}
.el-date-range-picker.has-sidebar .el-picker-panel__body {
  margin-left: 0 !important;
  width: 646px !important;
  flex-shrink: 0;
}

/* Hide out-of-range year/month cells in picker panel (not just disabled/greyed) */
.el-year-table td.disabled { visibility: hidden; }
.el-month-table td.disabled { visibility: hidden; }
.el-message {
  padding: 10px 16px;
  border-radius: 4px;
  font-size: 14px;
  max-width: 280px;
}

.el-message__icon {
  font-size: 16px;
  margin-right: 8px;
}

body .el-select-dropdown__item {
  font-family: "Inter Variable", sans-serif;
  font-size: 14px !important;
}

body .el-select .el-input__inner {
  font-family: "Inter Variable", sans-serif;
}

.el-message__content {
  line-height: 1.5;
}
.cleanlinessScoreBoxPopper_Content {
  background: #fff;
  padding: 17px 15px;
  b {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 4px;
  }
  div {
    font-family: Inter, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: #8f8f8f;
    margin-bottom: 0;
  }
}
.echartsPositiveLegend {
  padding:17px 15px !important;
  b {
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #2faa68;
    margin-right: 8px;
  }
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #8f8f8f;
}
.echartsNeutralLegend {
  padding:17px 15px !important;
  b {
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #efb82c;
    margin-right: 8px;
  }
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #8f8f8f;
}
.echartsNegativeLegend {
  padding:17px 15px !important;
  b {
    display: inline-block;
    width: 8px;
    height: 8px;
    background: #ce4049;
    margin-right: 8px;
  }
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #8f8f8f;
}
div[class*="Vue-Toastification__container"],
div[class*="Vue-Toastification__container"] > div {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed !important;
  top: 20px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  z-index: 999999 !important;
  // width: auto !important;
  // max-width: 100% !important;
  height: auto !important;
  margin: 0 !important;
  padding: 10px !important;
  border: #eaf6e3 !important;
  // color: #74c971 !important;
}
.Vue-Toastification__icon {
  margin-right: 8px !important; /* 控制图标和文字之间的距离 */
}
/*成功*/
div[class*="Vue-Toastification__toast"].custom-success-toast {
  background-color: #f0f9eb !important;
  border: 1px solid #eaf6e3 !important;
  color: #74c971 !important;
  font-family: "Inter Variable", sans-serif;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  padding: 10px !important;
  min-height: 40px !important;
  white-space: nowrap !important;
  max-width: 1000px !important;
}

.custom-success-toast .Vue-Toastification__progress-bar {
  position: absolute;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background-color: #74c971 !important;
  width: 100% !important;
  height: 2px !important;
}

.custom-success-toast .Vue-Toastification__text {
  color: #74c971 !important;
}
/*信息*/
div[class*="Vue-Toastification__toast"].custom-info-toast {
  background-color: #eff6ff !important;
  border: 1px solid #dbeafe !important;
  color: #3b82f6 !important;
  font-family: "Inter Variable", sans-serif;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  padding: 10px !important;
  min-height: 40px !important;
  white-space: nowrap !important;
  max-width: 1000px !important;
  overflow: hidden !important;
  position: relative !important;
}

.custom-info-toast .Vue-Toastification__progress-bar {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  background-color: #3b82f6 !important;
  height: 2px !important;
}

.custom-info-toast .Vue-Toastification__text {
  color: #3b82f6 !important;
}

.custom-info-toast .Vue-Toastification__toast-body {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

.custom-info-toast .Vue-Toastification__toast-body::before {
  content: '';
  display: block;
  width: 14px;
  height: 14px;
  min-width: 14px;
  flex-shrink: 0;
  border: 2px solid #dbeafe;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: custom-info-spin 0.8s linear infinite;
}

@keyframes custom-info-spin {
  to { transform: rotate(360deg); }
}

.custom-info-toast .Vue-Toastification__icon {
  display: none !important;
}
/*警告*/
div[class*="Vue-Toastification__toast"].custom-warning-toast {
  background-color: #fdf6ec !important; /* 警告提示背景色（浅黄） */
  border: 1px solid #ffe58f !important; /* 警告提示边框色（深黄） */
  color: #e6a23c !important;
  font-family: "Inter Variable", sans-serif;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  padding: 10px !important;
  min-height: 40px !important;
  white-space: nowrap !important;
  max-width: 1000px !important;
  position: relative;
}

.custom-warning-toast .Vue-Toastification__progress-bar {
  position: absolute;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background-color: #faad14 !important;
  height: 2px !important;
}

.custom-warning-toast .Vue-Toastification__text {
  color: #e6a23c !important;
}
//error
div[class*="Vue-Toastification__toast"].custom-error-toast {
  background-color: #fef0f0 !important;
  border: 1px solid #ffccc7 !important;
  color: #f67c7c !important;
  font-family: "Inter Variable", sans-serif;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  padding: 10px !important;
  white-space: nowrap !important;
  max-width: 1000px !important;
  min-height: 40px !important;

  position: relative;
}

.custom-error-toast .Vue-Toastification__progress-bar {
  position: absolute;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background-color: #ff4d4f !important;
  height: 2px !important;
}

.custom-error-toast .Vue-Toastification__text {
  color: #f67c7c !important;
}
/* 默认提示（无指定类型时使用） */
div[class*="Vue-Toastification__toast"].custom-default-toast {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
  color: #495057 !important;
  font-family: "Inter Variable", sans-serif;
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  padding: 10px !important;
  min-height: 40px !important;
  white-space: nowrap !important;
  max-width: 1000px !important;
  position: relative;
}

.custom-default-toast .Vue-Toastification__progress-bar {
  position: absolute;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background-color: #adb5bd !important;
  height: 2px !important;
}

.custom-default-toast .Vue-Toastification__text {
  color: #495057 !important;
}

/* custom-notice-toast — 非加载、非报错的普通说明（如报告页「该酒店暂无中文报告」）。
   和页面卡片同一套外观：白底、暖灰边框、深蓝信息图标；关闭按钮可见，顶部细进度条。 */
div[class*="Vue-Toastification__toast"].custom-notice-toast {
  background-color: #ffffff !important;
  border: 1px solid #E8E4DC !important;
  color: #232323 !important;
  font-family: "Inter Variable", Inter, sans-serif;
  border-radius: 8px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08) !important;
  padding: 10px 12px 10px 14px !important;
  align-items: center;
  gap: 10px;
}
.custom-notice-toast .Vue-Toastification__icon {
  margin: 0 !important;
  width: auto !important;
  height: auto !important;
  font-size: 16px;
  color: #1A365D;
}
.custom-notice-toast .Vue-Toastification__toast-body {
  font-size: 14px !important;
  line-height: 20px !important;
  color: #232323 !important;
}
.custom-notice-toast .Vue-Toastification__close-button {
  color: #6B7280 !important;
  opacity: 1 !important;
  font-size: 18px !important;
  font-weight: 400 !important;
  line-height: 20px !important;
  padding-left: 8px !important;
}
.custom-notice-toast .Vue-Toastification__close-button:hover {
  color: #1A365D !important;
}
/* 顶部 2px 倒计时条（和 custom-info-toast 一样贴顶）。别 display:none：
   库的自动关闭靠这条动画的 animationend 触发。 */
/* main.js 里 transition 写的是 "bounce"，离场 class 实际是 bounce-leave-active，
   toast.css 里只有 Vue-Toastification__bounce-* 的动画，对不上 —— 所以离场时什么动画都没有，
   提示原样停 750ms（库写死的 transitionDuration）才被移除，点 × 像卡了一秒。
   这条提示给它一个 0.2s 淡出：0.2s 后就看不见了，750ms 时元素才从 DOM 拿掉。 */
.bounce-leave-active.custom-notice-toast {
  animation: custom-notice-out 0.2s ease-in forwards !important;
}
@keyframes custom-notice-out {
  to {
    opacity: 0;
    transform: translate3d(0, -8px, 0);
  }
}
.custom-notice-toast .Vue-Toastification__progress-bar {
  top: 0 !important;
  bottom: auto !important;
  left: 0 !important;
  height: 2px !important;
  background-color: #1A365D !important;
  border-top-left-radius: 8px;
}




.MainBodyCategoryType .el-cascader-node .el-radio.is-disabled ~ .el-cascader-node__label{
  color: #C0C4CC !important;
}
.MainBodyCategoryType .el-cascader-node .el-radio.is-disabled ~ i{
  color: #C0C4CC !important;
}
.MainBodyCategoryType{
  padding:5px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 2100 !important; /* must exceed sticky filter bar z-index (max 1010) */
  .el-scrollbar{
    height: 100%;
    overflow-x: hidden;
  }
  li{
    display: flex;
    justify-content: left;
    align-items: center;
    padding:0 8px ;
    border-radius: 4px;
    font-size: 14px !important;
    height: 40px;
    line-height: 38px;
    span{
      font-family: 'Inter',sans-serif !important;
      font-weight: 400;
    }
    // 类别图标：2026-09 改版的线框图标没有外圈、线条铺满整个框，同样 24px 比旧的带圈图标大一号，用 20px
    img{
      margin-right: 8px;
      display: block;
      width: 20px;
      height: 20px;
    }
  }
  .el-scrollbar__wrap{
    max-height: 1000px !important;
    overflow: hidden;
    width: 100%;
  }
  .popper__arrow{
    display: none !important;
  }
}
.RestaurantCategoryType{
  padding:5px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 2100 !important;
  .el-scrollbar{
    height: 100%;
    overflow-x: hidden;
  }
  li{
    display: flex;
    justify-content: left;
    align-items: center;
    padding:0 8px ;
    border-radius: 4px;
    font-size: 14px !important;
    height: 40px;
    line-height: 38px;
    span{
      font-family: 'Inter',sans-serif !important;
      font-weight: 400;
    }
    img{
      margin-right: 8px;
      display: block;
      width: 24px;
      height: 24px;
    }
  }
  .el-scrollbar__wrap{
    max-height: 1000px !important;
    overflow: hidden;
  }
  li:hover,
  .el-select-dropdown__item:hover {
    background: var(--biz-hover-bg) !important;
  }
  .selected{
    background: var(--biz-selected-bg) !important;
    font-weight: 400;
    color: var(--biz-text) !important;
  }
  .popper__arrow{
    display: none !important;
  }
}

/* Global: hover uses biz color, focus resets to default */
.el-select .el-input__inner:hover,
.el-input__inner:hover {
  border-color: var(--biz-border-hover) !important;
}
.el-select .el-input__inner:focus,
.el-select.is-focus .el-input__inner,
.el-input.is-focus .el-input__inner,
.el-input__inner:focus {
  border-color: #D9D9D9 !important;
  outline: none !important;
  box-shadow: none !important;
}
/* Global el-switch active color — override inline active-color prop */
.el-switch.is-checked .el-switch__core {
  border-color: var(--biz-color) !important;
  background-color: var(--biz-color) !important;
}
.el-checkbox__input.is-indeterminate .el-checkbox__inner {
    background-color: var(--biz-color) !important;
    border-color: var(--biz-color) !important;
  }
/* stickyTop classes are now handled per-component with position: sticky */
.F2F7FFBg{
  background: var(--color-accent-light) !important;
}

/* ── Global Tooltip / Popover Z-index & Content Baseline ───────────────────
 * Problem: BrandOverview sticky bar was at z-index 2010 (above el-popper
 * default of 2000), causing tooltips to be hidden behind the filter bar.
 * Fix: sticky bars are capped at 1010. Popovers start at 2000 and go up.
 *
 * These rules establish a visible content baseline so tooltip text is never
 * accidentally invisible due to inherited color/size issues. We do NOT set
 * z-index here — Element UI's PopupManager assigns z-index dynamically via
 * inline style, and overriding it with !important would break nested dialogs.
 * ──────────────────────────────────────────────────────────────────────── */

/* Tooltip content: guarantee visible text regardless of inherited color */
.el-tooltip__popper {
  font-family: "Inter Variable", Inter, sans-serif !important;
  font-size: 14px !important;
  line-height: 20px !important;
  padding: 12px 16px !important;
  max-width: 320px; 
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal !important;
}

/* Dark tooltip variant (effect="dark") — design-system standard */
.el-tooltip__popper.is-dark {
  background: var(--tooltip-bg) !important;
  color: var(--tooltip-text) !important;
  border-radius: var(--tooltip-radius) !important;
  box-shadow: var(--tooltip-shadow) !important;
  border: none !important;
}
/* No arrow — dark tooltips render as clean rounded rectangles only */
.el-tooltip__popper.is-dark .popper__arrow,
.el-tooltip__popper.is-dark .popper__arrow::after {
  display: none !important;
}

/* Light tooltip variant — kept for legacy components, not used in main dashboard */
.el-tooltip__popper.is-light {
  background: #ffffff !important;
  color: #232323 !important;
  border-color: #1a365d !important;
}

/* AppPopover — design-system popover style
 * Premium white card: subtle warm background, refined border, layered shadow.
 * Used for multi-sentence explanations (risk signals, KPI definitions, etc.)
 */
.app-popover.el-popover {
  background: #FAFAF9 !important;
  border: 1px solid #E2DDD6 !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.10) !important;
  padding: 14px 16px !important;
  font-family: "Inter Variable", Inter, sans-serif !important;
  font-size: 13px !important;
  line-height: 21px !important;
  color: #374151 !important;
}
.app-popover.el-popover p {
  margin: 0;
  font-size: 13px;
  line-height: 21px;
  color: #374151;
}
/* No arrow/tail — clean floating card */
.app-popover .popper__arrow,
.app-popover .popper__arrow::after {
  display: none !important;
}

/* Popover: guarantee readable body text.
 * NOTE: color intentionally has no !important so component-level dark popover
 * variants (MyElPopover, TonePopover) can override with their own color: #fff. */
.el-popover {
  font-family: "Inter Variable", Inter, sans-serif !important;
  font-size: 14px !important;
  line-height: 22px !important;
  color: #232323;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  white-space: normal !important;
  min-width: inherit !important;
}

/* Guard: suppress rendering an empty popover shell */
.el-popover:empty {
  display: none !important;
}


.BrandOverviewEchartsTooltip{
  padding: 16px;
  background: #FFFFFF;
  font-family: Inter,sans-serif !important;
  .TopText{
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 8px;
    color: #232323;
  }
  .botBox{
    display: flex;
    justify-content: left;
    align-items: center;
    gap: 8px;
    div{
      display: block;
      width: 8px;
      height: 8px;
    }
    p{
      margin: 0;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      color: #6A6969;
    }
    b{
      color: #232323;
      font-weight: 600;
      font-size: 14px;
      line-height: 22px;

    }
  }

}
.BrandOverviewYAxisTooltip{
  background: #000;
  padding: 6px 8px;
  border-radius: 2px;
  color: #fff;
  font-family: Inter,sans-serif !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
}


.CustomerJourneyTooltip{
  //width: 368px;
  height:auto;
  padding: 12px;
  font-family: Inter, sans-serif !important;
  background: #fff;
  border-radius: 4px;
  .date{
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #6A6969;
  }
  .CustomerJourneyTooltipList{
    position:relative;
    padding-right: 30px;
    b{
      display: inline-block;
      width: 8px;
      height: 3px;
      margin-right: 8.5px;
      background: #1A365D;
      vertical-align: middle;
    }
    p{
      display: inline-block;
      //width: 294px;
      font-weight: 400;
      font-size: 14px;
      line-height: 22px;
      vertical-align: middle;
      color: #6A6969;
      margin: 4px 0;
      margin-right: 15px;
    }
    span{
      color:#232323;
      position: absolute;
      top: 4px;
      right: 0;
      font-family: InterMedium, sans-serif !important;
      font-weight: 500;
      font-size: 14px;
      float: right;
      line-height: 22px;
      text-align: right;
      vertical-align: middle;
      display: inline-block;
      width: 27px;
    }
  }
}

.Reviews_FC_popover{
  .popper__arrow{
    display: none ;
  }
  .content{
    white-space: nowrap;
  }
}


/* ===== Global Card Modernization ===== */
/* Override old card border style globally with shadow + larger radius */
.el-loading-mask{
  border-radius: 12px !important;
  background: #fff !important;
}

.ToneSelectOption {
  padding: 8px;
  .popper__arrow{
    display: none !important;
  }
  .el-select-dropdown__item {
    font-size: 14px !important;
    line-height: 40px !important;
    height: 40px !important;
    padding-left: 9px;
    border-radius: 4px;
  }
  .ant-select-dropdown-menu-item {
    margin: 1px 0;
  }
  .selected,
  .ant-select-dropdown-menu-item-selected {
    background: var(--color-accent-light);
    // color: var(--color-accent-primary);
    font-weight: 400;
    border-radius: 4px;
  }
  .el-select-dropdown__item:hover {
    background-color: #F0F4F8 !important;
    color: #1A365D !important;
  }
}

/* t-select dropdown (Greeting style / Default greeting) — match ToneSelectOption design */
.t-select__list {
  padding: 8px !important;
  font-family: "Inter", sans-serif !important;
}
.t-select-option, .t-input {
  font-size: 14px !important;
  line-height: 40px !important;
  height: 40px !important;
  border-radius: 4px !important;
  padding: 0 9px !important;
  color: #232323 !important;
  font-weight: 400 !important;
}
.t-select-option:not(.t-is-disabled):not(.t-is-selected):hover {
  background: #F0F4F8 !important;
  color: #232323 !important;
}
.t-select-option.t-is-selected:not(.t-is-disabled):hover {
  background-color: #F0F4F8 !important;
  color: #1A365D !important;
  font-weight: 400 !important;
}
.t-checkbox.t-is-checked .t-checkbox__input {
  background-color: #1A365D !important;
}
.t-select-option.t-is-selected:not(.t-is-disabled) .t-checkbox__label,
.t-select .t-fake-arrow--active,
.el-dialog__headerbtn:focus .el-dialog__close, .el-dialog__headerbtn:hover .el-dialog__close
 {
  color: #1A365D !important;
}
.t-select-option:not(.t-is-disabled):not(.t-is-selected):hover .t-checkbox__input,
.t-checkbox.t-is-checked .t-checkbox__input, .t-input:hover:not(.t-is-disabled),.t-input--focused {
  border-color: var(--biz-border-hover) !important;
}
.t-tag-input--break-line:not(.t-is-empty) .t-input {
  display: flex !important;
}
.t-select-option.t-is-selected:not(.t-is-disabled) {
  background-color: #e6eef6 !important;
}
</style>
